import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Available languages
export const LANGUAGES: Record<string, { code: string; name: string; flag: string }> = {
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  nb: { code: 'nb', name: 'Norsk', flag: '🇳🇴' }
};

// Map language codes for consistency
const mapLanguageCode = (code: string | null | undefined): string => {
  if (!code) return 'nb';
  if (code === 'no') return 'nb';
  return code;
};

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, params?: Record<string, any>) => string;
  languages: typeof LANGUAGES;
  currentLanguage: { code: string; name: string; flag: string };
  i18n: typeof i18n;
  isLoading: boolean;
}

interface LanguageProviderProps {
  children: ReactNode;
}

// Create context
const LanguageContext = createContext<LanguageContextType>({} as LanguageContextType);

// Translation hook - now uses react-i18next
export const useTranslation = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider');
  }
  return context;
};

// Provider component - bridges existing API with react-i18next
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem('lexy_language') || localStorage.getItem('language');
    return mapLanguageCode(saved);
  });
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetchedFromServer, setHasFetchedFromServer] = useState(false);

  // Fetch user language preference from server on mount
  useEffect(() => {
    const fetchUserLanguage = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          const serverLanguage = userData.settings?.language;
          if (serverLanguage) {
            const mappedLang = mapLanguageCode(serverLanguage);
            // Only update if different from current
            if (mappedLang !== language) {
              setLanguageState(mappedLang);
              localStorage.setItem('lexy_language', mappedLang);
              localStorage.setItem('language', mappedLang);
              i18n.changeLanguage(mappedLang);
              document.documentElement.lang = mappedLang;
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch user language preference:', error);
      } finally {
        setIsLoading(false);
        setHasFetchedFromServer(true);
      }
    };

    if (!hasFetchedFromServer) {
      fetchUserLanguage();
    }
  }, [hasFetchedFromServer, language]);

  // Save language preference to server
  const saveLanguageToServer = useCallback(async (lang: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/api/auth/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ language: lang })
      });
    } catch (error) {
      console.error('Failed to save language preference to server:', error);
    }
  }, []);

  // Sync language changes with i18n and server
  const setLanguage = useCallback((newLang: string) => {
    const mappedLang = mapLanguageCode(newLang);
    setLanguageState(mappedLang);
    localStorage.setItem('lexy_language', mappedLang);
    localStorage.setItem('language', mappedLang);
    i18n.changeLanguage(mappedLang);
    document.documentElement.lang = mappedLang;
    // Save to server (fire and forget)
    saveLanguageToServer(mappedLang);
  }, [saveLanguageToServer]);

  // Initialize i18n language on mount
  useEffect(() => {
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
  }, [language]);

  // Translation function - uses react-i18next under the hood
  // Maps legacy dot-notation keys to namespaced keys
  const t = useCallback((key, params = {}) => {
    // Check if key uses namespace syntax (e.g., "common:buttons.save")
    if (key.includes(':')) {
      const translated = i18n.t(key, params);
      return translated !== key ? translated : key;
    }
    
    // Legacy key mapping to namespaces (dot notation)
    const namespaceMap = {
      'common.': 'common:buttons.',
      'sidebar.': 'nav:sidebar.',
      'dashboard.': 'dashboard:',
      'inbox.': 'inbox:',
      'precase.': 'triage:precase.',
      'cases.': 'cases:',
      'priority.': 'common:priority.',
      'risk.': 'caseQueue:risk.',
      'category.': 'category:',
      'settings.': 'settings:',
      'auth.': 'common:auth.',
      'chat.': 'chat:ai.',
      'time.': 'common:time.',
      'triage.': 'triage:',
      'overview.': 'inbox:overview.',
      'email.': 'inbox:email.',
      'caseSelector.': 'inbox:caseSelector.',
      'outlook.': 'outlook:',
      'caseQueue.': 'caseQueue:',
      'matching.': 'outlook:matching.',
      'actions.': 'inbox:actions.'
    };
    
    // Try to find matching namespace (for dot-notation keys)
    for (const [prefix, namespace] of Object.entries(namespaceMap)) {
      if (key.startsWith(prefix)) {
        const newKey = namespace + key.slice(prefix.length);
        const translated = i18n.t(newKey, params);
        if (translated !== newKey) {
          return translated;
        }
      }
    }
    
    // For single-word keys, try multiple namespaces
    if (!key.includes('.')) {
      // Try dashboard namespace first (most common for dashboard page)
      const dashboardTranslated = i18n.t(`dashboard:${key}`, params);
      if (dashboardTranslated !== `dashboard:${key}`) {
        return dashboardTranslated;
      }
      
      // Try common buttons
      const buttonTranslated = i18n.t(`common:buttons.${key}`, params);
      if (buttonTranslated !== `common:buttons.${key}`) {
        return buttonTranslated;
      }
      
      // Try common status
      const statusTranslated = i18n.t(`common:status.${key}`, params);
      if (statusTranslated !== `common:status.${key}`) {
        return statusTranslated;
      }
      
      // Try common priority
      const priorityTranslated = i18n.t(`common:priority.${key}`, params);
      if (priorityTranslated !== `common:priority.${key}`) {
        return priorityTranslated;
      }
    }
    
    // Try the key directly in i18n
    const directTranslated = i18n.t(key, params);
    if (directTranslated !== key) {
      return directTranslated;
    }
    
    // Fallback to key
    return key;
  }, []);

  const value = {
    language,
    setLanguage,
    t,
    languages: LANGUAGES,
    currentLanguage: LANGUAGES[language] || LANGUAGES.nb,
    // Expose i18n for advanced usage
    i18n,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
